import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Coins, Loader2, Search, ShieldCheck, UserCheck, Ticket, Plus, Power } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmtTs = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};
const ACTIONS = [
  { key: "grant", label: "Grant", verb: "Grant", color: "#39FF14" },
  { key: "deduct", label: "Deduct", verb: "Remove", color: "#FF007F" },
  { key: "set", label: "Set balance", verb: "Set", color: "#00E5FF" },
];
const TYPE_LABEL = { manual_grant: "Grant", deduction: "Deduct", adjustment: "Set", promo: "Promo" };

export default function AdminCredits() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("grant");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [working, setWorking] = useState(false);
  const [txns, setTxns] = useState([]);

  // promos
  const [promos, setPromos] = useState([]);
  const [pAmount, setPAmount] = useState("");
  const [pCode, setPCode] = useState("");
  const [pMax, setPMax] = useState("");
  const [pExpiry, setPExpiry] = useState("");
  const [pNote, setPNote] = useState("");
  const [creatingPromo, setCreatingPromo] = useState(false);

  useEffect(() => { if (user && !user.is_staff) navigate("/dashboard"); }, [user, navigate]);

  const loadTxns = useCallback(() => {
    api.get("/admin/credits/transactions?limit=50").then(({ data }) => setTxns(data.transactions || [])).catch(() => {});
  }, []);
  const loadPromos = useCallback(() => {
    api.get("/admin/promos").then(({ data }) => setPromos(data.promos || [])).catch(() => {});
  }, []);
  useEffect(() => { loadTxns(); loadPromos(); }, [loadTxns, loadPromos]);

  const doSearch = async (e) => {
    e?.preventDefault?.();
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(query.trim())}&limit=20`);
      setResults(data.users || []);
    } catch (err) {
      toast.error(err.response?.status === 403 ? "Admin access only" : "Search failed");
    } finally { setSearching(false); }
  };

  const amountValid = /^\d+$/.test(amount) && (mode === "set" ? parseInt(amount, 10) >= 0 : parseInt(amount, 10) > 0);
  const action = ACTIONS.find((a) => a.key === mode);

  const openConfirm = () => {
    if (!selected) return toast.error("Select a user first");
    if (!amountValid) return toast.error(mode === "set" ? "Enter a whole number (0 or more)" : "Enter a whole number greater than 0");
    setConfirmOpen(true);
  };

  const perform = async () => {
    if (working) return;
    setWorking(true);
    try {
      const amt = parseInt(amount, 10);
      const ep = mode === "grant" ? "/admin/credits/grant" : mode === "deduct" ? "/admin/credits/deduct" : "/admin/credits/set";
      const body = mode === "set" ? { userId: selected.id, balance: amt, reason: reason.trim() } : { userId: selected.id, amount: amt, reason: reason.trim() };
      const { data } = await api.post(ep, body);
      toast.success(`${selected.email} → new balance ${data.balance_after}.`);
      setSelected(data.target);
      setResults((r) => r.map((u) => (u.id === data.target.id ? data.target : u)));
      setAmount(""); setReason(""); setConfirmOpen(false);
      loadTxns(); refreshUser();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Action failed");
      setConfirmOpen(false);
    } finally { setWorking(false); }
  };

  const createPromo = async (e) => {
    e?.preventDefault?.();
    if (!/^\d+$/.test(pAmount) || parseInt(pAmount, 10) <= 0) return toast.error("Enter a credit amount greater than 0");
    setCreatingPromo(true);
    try {
      const body = { amount: parseInt(pAmount, 10), note: pNote.trim() };
      if (pCode.trim()) body.code = pCode.trim();
      if (pMax.trim()) body.max_redemptions = parseInt(pMax, 10);
      if (pExpiry) body.expires_at = pExpiry;
      const { data } = await api.post("/admin/promos", body);
      toast.success(`Code ${data.promo.code} created (${data.promo.amount} credits).`);
      setPAmount(""); setPCode(""); setPMax(""); setPExpiry(""); setPNote("");
      loadPromos();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not create code");
    } finally { setCreatingPromo(false); }
  };

  const togglePromo = async (code) => {
    try {
      const { data } = await api.post(`/admin/promos/${encodeURIComponent(code)}/toggle`);
      toast.success(`${code} ${data.active ? "activated" : "deactivated"}`);
      loadPromos();
    } catch { toast.error("Could not update code"); }
  };

  const newBalPreview = selected && !selected.is_owner
    ? (mode === "grant" ? (selected.credits || 0) + (parseInt(amount, 10) || 0)
      : mode === "deduct" ? Math.max(0, (selected.credits || 0) - (parseInt(amount, 10) || 0))
        : (parseInt(amount, 10) || 0))
    : "unlimited";

  return (
    <div className="min-h-screen bg-background text-white">
      <TopNav />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-white/50 hover:text-white text-sm mb-6" data-testid="admin-credits-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-[#39FF14]" />
          <h1 className="font-display text-3xl font-semibold tracking-tight">Credit Management</h1>
        </div>
        <p className="text-white/50 text-sm mb-8">Owner/Admin only — grant, deduct or set credit balances, and manage promo codes. Every change is audited.</p>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Search + select */}
          <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-5" data-testid="user-search-panel">
            <form onSubmit={doSearch} className="flex gap-2">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by email, name or user ID"
                className="bg-black border-white/15" data-testid="user-search-input" />
              <Button type="submit" disabled={searching} className="rounded-full bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 shrink-0" data-testid="user-search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </form>
            <div className="mt-4 space-y-1.5 max-h-72 overflow-auto" data-testid="user-results">
              {results.length === 0 && <p className="text-white/40 text-sm py-6 text-center">No users — search above.</p>}
              {results.map((u) => (
                <button key={u.id} onClick={() => setSelected(u)}
                  className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${selected?.id === u.id ? "border-[#39FF14]/60 bg-[#39FF14]/5" : "border-white/10 hover:bg-white/5"}`}
                  data-testid={`user-result-${u.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm truncate">{u.email}</span>
                    <span className="text-[11px] text-white/50 shrink-0 flex items-center gap-1"><Coins className="w-3 h-3 text-[#39FF14]" />{u.is_owner ? "∞" : u.credits}</span>
                  </div>
                  <div className="text-[11px] text-white/40 truncate">{u.name} · {u.role}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Action form */}
          <div className="rounded-2xl border border-white/10 bg-zinc-950/60 p-5" data-testid="grant-panel">
            {!selected ? (
              <div className="flex flex-col items-center justify-center h-full text-white/40 text-sm py-12">
                <UserCheck className="w-8 h-8 mb-2 opacity-40" />Select a user to manage credits.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="rounded-xl border border-white/10 bg-black/40 p-4" data-testid="selected-user">
                  <div className="text-sm font-medium truncate" data-testid="selected-user-email">{selected.email}</div>
                  <div className="text-[11px] text-white/40">{selected.name} · {selected.role}</div>
                  <div className="mt-2 flex items-center gap-1.5 text-sm"><Coins className="w-4 h-4 text-[#39FF14]" />
                    Current balance: <span className="font-semibold" data-testid="selected-user-balance">{selected.is_owner ? "Unlimited" : selected.credits}</span>
                  </div>
                </div>
                <div className="flex gap-1.5 p-1 rounded-full bg-black/40 border border-white/10" data-testid="action-mode">
                  {ACTIONS.map((a) => (
                    <button key={a.key} onClick={() => setMode(a.key)}
                      className={`flex-1 rounded-full py-1.5 text-xs font-medium transition-colors ${mode === a.key ? "bg-white/10 text-white" : "text-white/45 hover:text-white/70"}`}
                      data-testid={`mode-${a.key}`}>{a.label}</button>
                  ))}
                </div>
                <div>
                  <label className="text-[11px] text-white/45 uppercase tracking-wide">{mode === "set" ? "New exact balance" : mode === "deduct" ? "Credits to remove" : "Credits to grant"}</label>
                  <Input value={amount} onChange={(e) => setAmount(e.target.value.replace(/[^\d]/g, ""))} inputMode="numeric"
                    placeholder={mode === "set" ? "e.g. 0" : "e.g. 100"} className="bg-black border-white/15 mt-1" data-testid="grant-amount-input" />
                </div>
                <div>
                  <label className="text-[11px] text-white/45 uppercase tracking-wide">Reason / note (optional)</label>
                  <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                    placeholder="e.g. Loyalty bonus" className="bg-black border-white/15 mt-1" data-testid="grant-reason-input" />
                </div>
                <Button onClick={openConfirm} disabled={!amountValid || working}
                  style={{ backgroundColor: action.color }}
                  className="w-full rounded-full text-black hover:opacity-90 font-semibold disabled:opacity-40" data-testid="grant-credits-btn">
                  {working ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working…</> : <><Coins className="w-4 h-4 mr-1.5" />{action.verb} credits</>}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Recent activity */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-zinc-950/60 overflow-hidden" data-testid="grant-history">
          <div className="px-4 py-3 border-b border-white/10 text-sm font-medium">Recent credit activity</div>
          <div className="grid grid-cols-[1fr_1.2fr_auto_auto_1fr_1fr] gap-2 px-4 py-2 text-[11px] uppercase tracking-wide text-white/40 border-b border-white/10">
            <span>When</span><span>User</span><span>Type</span><span className="text-right">Δ</span><span className="text-right">New bal.</span><span>By / reason</span>
          </div>
          {txns.length === 0 && <div className="px-4 py-8 text-center text-white/40 text-sm">No activity yet.</div>}
          {txns.map((t, i) => (
            <div key={t.id} className="grid grid-cols-[1fr_1.2fr_auto_auto_1fr_1fr] gap-2 px-4 py-2.5 text-sm border-b border-white/5 last:border-0" data-testid={`grant-row-${i}`}>
              <span className="text-white/50">{fmtTs(t.created_at)}</span>
              <span className="text-white/80 truncate">{t.target_user_email}</span>
              <span className="text-white/50 text-xs">{TYPE_LABEL[t.type] || t.type}</span>
              <span className={`text-right font-medium ${t.amount >= 0 ? "text-[#39FF14]" : "text-[#FF007F]"}`}>{t.amount >= 0 ? "+" : ""}{t.amount}</span>
              <span className="text-right text-white/70">{t.balance_after}</span>
              <span className="text-white/40 truncate">{t.granted_by_email}{t.reason ? ` · ${t.reason}` : ""}</span>
            </div>
          ))}
        </div>

        {/* Promo codes */}
        <div className="mt-8 rounded-2xl border border-white/10 bg-zinc-950/60 p-5" data-testid="promo-panel">
          <div className="flex items-center gap-2 mb-4">
            <Ticket className="w-4 h-4 text-[#FFD54A]" />
            <h2 className="text-base font-medium">Promo codes</h2>
          </div>
          <form onSubmit={createPromo} className="grid sm:grid-cols-[repeat(4,1fr)_auto] gap-2 items-end" data-testid="promo-create-form">
            <div>
              <label className="text-[11px] text-white/45 uppercase tracking-wide">Credits *</label>
              <Input value={pAmount} onChange={(e) => setPAmount(e.target.value.replace(/[^\d]/g, ""))} inputMode="numeric" placeholder="100"
                className="bg-black border-white/15 mt-1" data-testid="promo-amount-input" />
            </div>
            <div>
              <label className="text-[11px] text-white/45 uppercase tracking-wide">Code (optional)</label>
              <Input value={pCode} onChange={(e) => setPCode(e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ""))} placeholder="auto"
                className="bg-black border-white/15 mt-1" data-testid="promo-code-input" />
            </div>
            <div>
              <label className="text-[11px] text-white/45 uppercase tracking-wide">Max uses</label>
              <Input value={pMax} onChange={(e) => setPMax(e.target.value.replace(/[^\d]/g, ""))} inputMode="numeric" placeholder="∞"
                className="bg-black border-white/15 mt-1" data-testid="promo-max-input" />
            </div>
            <div>
              <label className="text-[11px] text-white/45 uppercase tracking-wide">Expires</label>
              <Input type="date" value={pExpiry} onChange={(e) => setPExpiry(e.target.value)}
                className="bg-black border-white/15 mt-1" data-testid="promo-expiry-input" />
            </div>
            <Button type="submit" disabled={creatingPromo} className="rounded-full bg-[#FFD54A] text-black hover:bg-[#FFD54A]/90 font-semibold h-10" data-testid="promo-create-btn">
              {creatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" />Create</>}
            </Button>
          </form>
          <Input value={pNote} onChange={(e) => setPNote(e.target.value)} placeholder="Note (optional) — e.g. Black Friday campaign"
            className="bg-black border-white/15 mt-2 text-sm" data-testid="promo-note-input" />

          <div className="mt-5 space-y-1.5" data-testid="promo-list">
            {promos.length === 0 && <p className="text-white/40 text-sm py-4 text-center">No promo codes yet.</p>}
            {promos.map((p) => {
              const expired = p.expires_at && new Date(p.expires_at) < new Date();
              return (
                <div key={p.code} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-3 py-2" data-testid={`promo-row-${p.code}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-[#FFD54A]">{p.code}</span>
                      <span className="text-xs text-white/60 flex items-center gap-1"><Coins className="w-3 h-3 text-[#39FF14]" />{p.amount}</span>
                      {!p.active && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-white/50">inactive</span>}
                      {expired && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#FF007F]/15 text-[#FF007F]">expired</span>}
                    </div>
                    <div className="text-[11px] text-white/40 truncate">
                      {p.redemptions}{p.max_redemptions ? `/${p.max_redemptions}` : ""} redeemed
                      {p.expires_at ? ` · exp ${new Date(p.expires_at).toLocaleDateString()}` : ""}
                      {p.note ? ` · ${p.note}` : ""}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => togglePromo(p.code)}
                    className="h-8 rounded-full border-white/15 bg-transparent hover:bg-white/5 text-white text-xs shrink-0" data-testid={`promo-toggle-${p.code}`}>
                    <Power className="w-3.5 h-3.5 mr-1" />{p.active ? "Disable" : "Enable"}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-zinc-950 border-white/10 text-white" data-testid="grant-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {mode === "grant" && `Grant ${amount} credits to ${selected?.email}?`}
              {mode === "deduct" && `Remove ${amount} credits from ${selected?.email}?`}
              {mode === "set" && `Set ${selected?.email}'s balance to ${amount}?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              New balance will be {newBalPreview}. This is audited.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5" data-testid="grant-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); perform(); }} disabled={working}
              style={{ backgroundColor: action.color }} className="text-black hover:opacity-90" data-testid="grant-confirm-btn">
              {working ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Working…</> : `Confirm ${action.verb.toLowerCase()}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
